import type {
  FmFormFlowBlockDescriptor,
  FmFormFlowPageDescriptor,
} from './FmFormFlowPage.types';
import { validateFmActionConfirmation } from './FmActionConfirmation.validation';
import { assertFmEvidenceDescriptor } from './FmEvidence.validation';
import { validateFmFormFlowCollectionTable } from './FmFormFlowCollection.validation';
import {
  exactFormFlowKeys as exact,
  failFormFlow as fail,
  formFlowArray as array,
  formFlowBoolean as boolean,
  formFlowObject as object,
  formFlowOneOf as oneOf,
  formFlowText as text,
  nonEmptyFormFlowTextArray as nonEmptyTextArray,
  optionalFormFlowText as optionalText,
  uniqueFormFlowIds as uniqueIds,
} from './FmFormFlowValidation.shared';
import { blockBase, validateField, validateValue } from './FmFormFlowField.validation';
import { validateSummary } from './FmFormFlowSummary.validation';
import {
  refusePreviewedControls,
  requireDeclaredAction,
  validateFormFlowStateSurface,
} from './FmFormFlowState.validation';
import {
  FM_MAP_SELECTED_FEATURE_KINDS,
  FM_MEASUREMENT_PLOT_MAPPING_STATUSES,
} from '@/shared/config/mapSlotContracts';
import { hasMapSlot, isPrimaryMapSlotKey } from '@/shared/config/mapSlotRegistry';

const FORM_MAP_SELECTION_KINDS = FM_MAP_SELECTED_FEATURE_KINDS.filter((kind) => kind !== 'risk');

const ROOT_KEYS = new Set([
  'id',
  'ariaLabel',
  'title',
  'description',
  'backLabel',
  'state',
  'stateLabel',
  'empty',
  'error',
  'locked',
  'navigation',
  'summary',
  'blocks',
  'actions',
  'onEvent',
]);
const BLOCK_BASE_KEYS = ['id', 'title', 'description', 'state', 'stateLabel', 'control'] as const;

function validateStageStateAnchor(
  value: unknown,
  path: string,
  owned: ReadonlySet<string>,
  actions: Record<string, unknown>[],
): void {
  if (value === undefined) return;
  const anchor = object(value, path);
  exact(anchor, new Set(['blockId', 'resolvingActionId']), path);
  if (!owned.has(text(anchor.blockId, `${path}.blockId`))) {
    fail(`${path}.blockId`, 'must reference a block owned by this stage');
  }
  if (anchor.resolvingActionId === undefined) return;
  /* A notice that names an exit the flow does not offer is worse than prose: it
     renders a control that decides nothing. The SAME
     rule the other state surfaces use, not a second weaker one: checking only
     that the id is declared let a notice render a `disabled: true` action as a
     live button, because the renderer trusts the validator and never forwards
     `disabled` (review round 5). */
  /* Declared, not disabled, and not confirmation-gated — all three now live in
     `requireDeclaredAction`, so every state surface inherits them instead of the
     one that happened to be written last (review round 6). */
  requireDeclaredAction(anchor.resolvingActionId, `${path}.resolvingActionId`, actions);
}

/** Everything a stage story is judged against beyond the story itself. */
interface StageStoryScope {
  blockIds: readonly string[];
  actions: Record<string, unknown>[];
  descriptor: Record<string, unknown>;
  isActive: boolean;
}

function validateStageStory(value: unknown, path: string, scope: StageStoryScope): void {
  const { blockIds, actions, descriptor, isActive } = scope;
  const story = object(value, path);
  exact(
    story,
    new Set([
      'kind',
      'ownerQuestion',
      'literalAnswer',
      'dominantBeat',
      'requiredServedFields',
      'requiredProvenance',
      'primaryAction',
      'expertReveal',
      'transition',
      'state',
      'stateCopy',
      'stateAnchor',
      'mobileOrder',
      'conversionRole',
    ]),
    path,
  );
  if (story.kind !== 'story-transition') fail(`${path}.kind`, 'must be story-transition');
  for (const key of [
    'ownerQuestion',
    'literalAnswer',
    'primaryAction',
    'expertReveal',
    'transition',
  ]) {
    text(story[key], `${path}.${key}`);
  }
  oneOf(
    story.state,
    ['ready', 'loading', 'empty', 'partial', 'stale', 'locked', 'error'],
    `${path}.state`,
  );
  oneOf(
    story.conversionRole,
    ['input', 'orientation', 'evidence', 'declaration', 'confirmation', 'handoff'],
    `${path}.conversionRole`,
  );
  nonEmptyTextArray(story.requiredServedFields, `${path}.requiredServedFields`);
  nonEmptyTextArray(story.requiredProvenance, `${path}.requiredProvenance`);
  const owned = new Set(blockIds);
  const dominantBeat = text(story.dominantBeat, `${path}.dominantBeat`);
  if (!owned.has(dominantBeat))
    fail(`${path}.dominantBeat`, 'must reference a block owned by this stage');
  const mobileOrder = nonEmptyTextArray(story.mobileOrder, `${path}.mobileOrder`);
  if (new Set(mobileOrder).size !== mobileOrder.length)
    fail(`${path}.mobileOrder`, 'must not contain duplicates');
  if (mobileOrder.length !== blockIds.length || mobileOrder.some((id) => !owned.has(id))) {
    fail(`${path}.mobileOrder`, 'must contain every stage block exactly once');
  }
  const stateCopy = object(story.stateCopy, `${path}.stateCopy`);
  const stateKeys = ['loading', 'empty', 'partial', 'stale', 'locked', 'error'];
  exact(stateCopy, new Set(stateKeys), `${path}.stateCopy`);
  stateKeys.forEach((key) => text(stateCopy[key], `${path}.stateCopy.${key}`));
  validateStageStateAnchor(story.stateAnchor, `${path}.stateAnchor`, owned, actions);
  /* A STAGE-level lock previews its blocks exactly the way a flow-level one does
     — `stageAllowsControls` refuses `locked`, so the fieldset disables and the
     content stays on screen. Checking only the flow-level lock left that door
     wide open: a `map`, `files`, `select`, `date` or one-time-secret control on
     a stage-locked step is previewed and still operable (review round 5).

     Only the ACTIVE stage, for the same reason the flow-level rule narrows to
     the active item: a locked stage further down the flow renders nothing, so
     refusing its map would reject a legitimate descriptor for a preview that
     never happens (review round 6).

     `locked` ONLY, and not the renderer's `stageAllowsControls`, which round 6
     reached for on the reasoning that it disables the fieldset for `loading`
     too. Those two predicates answer different questions. The renderer's asks
     "are these controls inert right now"; this one asks "is this content being
     PREVIEWED behind a durable boundary the owner has not crossed". A loading
     stage withholds nothing — it is a transient wait — and refusing it turns an
     ordinary background refetch into a thrown descriptor: the measurement upload
     flow returns `stageState: 'loading'` while a property loads and ships an
     `evidence` collection on the same stage, so the widened rule would have
     taken that page down (review round 7). */
  if (isActive && story.state === 'locked') refusePreviewedControls(descriptor, owned);
}

function validateEditableCollectionLimits(
  control: Record<string, unknown>,
  controlPath: string,
): void {
  text(control.addLabel, `${controlPath}.addLabel`);
  text(control.removeLabel, `${controlPath}.removeLabel`);
  for (const key of ['minRows', 'maxRows'] as const) {
    if (
      control[key] !== undefined &&
      (!Number.isInteger(control[key]) || Number(control[key]) < 0)
    ) {
      fail(`${controlPath}.${key}`, 'must be a non-negative integer');
    }
  }
  if (
    typeof control.minRows === 'number' &&
    typeof control.maxRows === 'number' &&
    control.minRows > control.maxRows
  ) {
    fail(`${controlPath}.minRows`, 'must not exceed maxRows');
  }
}

// eslint-disable-next-line complexity -- fail-closed dispatch mirrors every governed capability in one auditable boundary.
function validateMapControl(control: Record<string, unknown>, controlPath: string): void {
  text(control.mode, `${controlPath}.mode`);
  const select = control.mode === 'select';
  if (!select && control.mode !== 'review') fail(`${controlPath}.mode`, 'is not registered');
  const slotKey = text(control.slotKey, `${controlPath}.slotKey`);
  const resolvedDataMap = select || isPrimaryMapSlotKey(slotKey);
  exact(
    control,
    new Set([
      'type',
      'mode',
      'slotKey',
      'propertyId',
      'sourceId',
      'layerIds',
      'selectedFeatureId',
      'emptyLabel',
      ...(select
        ? ['selectedFeatureKind', 'selectionLabel', 'measurementPlotRefs', 'preview', 'mapData']
        : ['sourceLabel', 'observedAt', ...(resolvedDataMap ? ['preview', 'mapData'] : [])]),
    ]),
    controlPath,
  );
  if (!hasMapSlot(slotKey)) fail(`${controlPath}.slotKey`, 'must reference a governed map slot');
  ['propertyId', 'sourceId', 'emptyLabel'].forEach((key) =>
    text(control[key], `${controlPath}.${key}`),
  );
  optionalText(control.selectedFeatureId, `${controlPath}.selectedFeatureId`);
  array(control.layerIds, `${controlPath}.layerIds`).forEach((id, layerIndex) =>
    text(id, `${controlPath}.layerIds[${layerIndex}]`),
  );
  if (select) {
    oneOf(
      control.selectedFeatureKind,
      FORM_MAP_SELECTION_KINDS,
      `${controlPath}.selectedFeatureKind`,
    );
    text(control.selectionLabel, `${controlPath}.selectionLabel`);
    if (control.measurementPlotRefs !== undefined) {
      const plotRefs = array(control.measurementPlotRefs, `${controlPath}.measurementPlotRefs`);
      uniqueIds(plotRefs, `${controlPath}.measurementPlotRefs`);
      plotRefs.forEach((value, plotIndex) => {
        const plotPath = `${controlPath}.measurementPlotRefs[${plotIndex}]`;
        const plot = object(value, plotPath);
        exact(plot, new Set(['id', 'label', 'mappingStatus', 'standId']), plotPath);
        text(plot.label, `${plotPath}.label`);
        oneOf(
          plot.mappingStatus,
          FM_MEASUREMENT_PLOT_MAPPING_STATUSES,
          `${plotPath}.mappingStatus`,
        );
        optionalText(plot.standId, `${plotPath}.standId`);
      });
    }
  } else {
    text(control.sourceLabel, `${controlPath}.sourceLabel`);
    optionalText(control.observedAt, `${controlPath}.observedAt`);
  }
  if (resolvedDataMap) {
    if (control.preview !== undefined) boolean(control.preview, `${controlPath}.preview`);
    if (control.preview === true && control.mapData !== null) {
      fail(`${controlPath}.mapData`, 'must be null for a deterministic preview');
    }
    if (control.preview !== true && (control.mapData === null || control.mapData === undefined)) {
      fail(`${controlPath}.mapData`, 'must contain resolved data for a production map');
    }
  }
  return;
}

/** One collection control, by mode. Split out of `validateBlock` to keep that
 * switch inside the complexity gate. */
function validateCollectionControl(control: Record<string, unknown>, controlPath: string): void {
  text(control.mode, `${controlPath}.mode`);
  if (control.mode === 'records') {
    exact(
      control,
      new Set(['type', 'mode', 'records', 'emptyLabel', 'selectionMode']),
      controlPath,
    );
    text(control.emptyLabel, `${controlPath}.emptyLabel`);
    if (control.selectionMode !== undefined)
      oneOf(control.selectionMode, ['none', 'single'], `${controlPath}.selectionMode`);
    const records = array(control.records, `${controlPath}.records`);
    uniqueIds(records, `${controlPath}.records`);
    records.forEach((value, recordIndex) => {
      const recordPath = `${controlPath}.records[${recordIndex}]`;
      const record = object(value, recordPath);
      exact(
        record,
        new Set(['id', 'title', 'description', 'statusLabel', 'selected', 'values']),
        recordPath,
      );
      text(record.title, `${recordPath}.title`);
      optionalText(record.description, `${recordPath}.description`);
      optionalText(record.statusLabel, `${recordPath}.statusLabel`);
      if (record.selected !== undefined) boolean(record.selected, `${recordPath}.selected`);
      const values = array(record.values, `${recordPath}.values`);
      uniqueIds(values, `${recordPath}.values`);
      values.forEach((item, itemIndex) =>
        validateValue(item, `${recordPath}.values[${itemIndex}]`),
      );
    });
    return;
  }
  if (control.mode === 'evidence') {
    exact(control, new Set(['type', 'mode', 'evidence', 'values']), controlPath);
    assertFmEvidenceDescriptor(control.evidence);
    const values = array(control.values, `${controlPath}.values`);
    uniqueIds(values, `${controlPath}.values`);
    values.forEach((item, itemIndex) => validateValue(item, `${controlPath}.values[${itemIndex}]`));
    return;
  }
  if (control.mode === 'review' || control.mode === 'edit') {
    const edit = control.mode === 'edit';
    exact(
      control,
      new Set(
        edit
          ? [
              'type',
              'mode',
              'columns',
              'rows',
              'emptyLabel',
              'addLabel',
              'removeLabel',
              'minRows',
              'maxRows',
            ]
          : ['type', 'mode', 'columns', 'rows', 'emptyLabel', 'caption'],
      ),
      controlPath,
    );
    text(control.emptyLabel, `${controlPath}.emptyLabel`);
    validateFmFormFlowCollectionTable(control.columns, control.rows, controlPath, edit);
    if (edit) {
      validateEditableCollectionLimits(control, controlPath);
    } else text(control.caption, `${controlPath}.caption`);
    return;
  }
  fail(`${controlPath}.mode`, 'is not registered');
  return;
}

function validateBlock(value: unknown, index: number): void {
  const path = `descriptor.blocks[${index}]`;
  const block = object(value, path) as FmFormFlowBlockDescriptor & Record<string, unknown>;
  blockBase(block, path);
  exact(block, new Set(BLOCK_BASE_KEYS), path);
  const controlPath = `${path}.control`;
  const control = object(block.control, controlPath);
  text(control.type, `${controlPath}.type`);
  switch (control.type) {
    case 'fields': {
      exact(control, new Set(['type', 'fields']), controlPath);
      const fields = array(control.fields, `${controlPath}.fields`);
      uniqueIds(fields, `${controlPath}.fields`);
      fields.forEach((field, fieldIndex) =>
        validateField(field, `${controlPath}.fields[${fieldIndex}]`),
      );
      return;
    }
    case 'collection': {
      validateCollectionControl(control, controlPath);
      return;
    }
    case 'map': {
      validateMapControl(control, controlPath);
      return;
    }
    case 'secret': {
      exact(
        control,
        new Set([
          'type',
          'secret',
          'secretLabel',
          'status',
          'copyLabel',
          'copiedLabel',
          'acknowledgeLabel',
          'warning',
        ]),
        controlPath,
      );
      ['secretLabel', 'copyLabel', 'copiedLabel', 'acknowledgeLabel', 'warning'].forEach((key) =>
        text(control[key], `${controlPath}.${key}`),
      );
      oneOf(control.status, ['available', 'copied', 'consumed'], `${controlPath}.status`);
      if (control.status === 'consumed') {
        if (control.secret !== null)
          fail(`${controlPath}.secret`, 'must be null after consumption');
      } else text(control.secret, `${controlPath}.secret`);
      return;
    }
    case 'notice':
      exact(control, new Set(['type', 'tone', 'dismissible']), controlPath);
      oneOf(control.tone, ['info', 'success', 'warning', 'error'], `${controlPath}.tone`);
      if (control.dismissible !== undefined)
        boolean(control.dismissible, `${controlPath}.dismissible`);
      return;
    default:
      fail(`${controlPath}.type`, `is not registered: "${String(control.type)}"`);
  }
}

function validateNavigation(
  value: unknown,
  blockIds: ReadonlySet<string>,
  actions: Record<string, unknown>[],
  descriptor: Record<string, unknown>,
): void {
  const navigation = object(value, 'descriptor.navigation');
  text(navigation.kind, 'descriptor.navigation.kind');
  const baseKeys = ['kind', 'ariaLabel', 'activeId', 'items'];
  if (navigation.kind === 'linear')
    exact(navigation, new Set([...baseKeys, 'invalidationMessage']), 'descriptor.navigation');
  else if (navigation.kind === 'sections')
    exact(navigation, new Set(baseKeys), 'descriptor.navigation');
  else fail('descriptor.navigation.kind', 'is not registered');
  text(navigation.ariaLabel, 'descriptor.navigation.ariaLabel');
  const activeId = text(navigation.activeId, 'descriptor.navigation.activeId');
  const items = array(navigation.items, 'descriptor.navigation.items');
  if (!items.length) fail('descriptor.navigation.items', 'must contain at least one item');
  const itemIds = uniqueIds(items, 'descriptor.navigation.items');
  if (!itemIds.has(activeId))
    fail('descriptor.navigation.activeId', 'must reference a navigation item');
  const owned = new Set<string>();
  let currentCount = 0;
  let activeIndex = -1;
  items.forEach((value, index) => {
    const path = `descriptor.navigation.items[${index}]`;
    const item = object(value, path);
    if (navigation.kind === 'linear') {
      exact(
        item,
        new Set([
          'id',
          'label',
          'description',
          'status',
          'statusLabel',
          'disabledReason',
          'blockIds',
          'story',
        ]),
        path,
      );
      oneOf(item.status, ['complete', 'current', 'available', 'blocked'], `${path}.status`);
      optionalText(item.statusLabel, `${path}.statusLabel`);
      if (item.status === 'current') currentCount += 1;
      if (item.id === activeId) activeIndex = index;
      optionalText(item.disabledReason, `${path}.disabledReason`);
      if (item.status === 'blocked' && item.disabledReason === undefined)
        fail(`${path}.disabledReason`, 'is required when blocked');
    } else {
      exact(item, new Set(['id', 'label', 'description', 'state', 'stateLabel', 'blockIds']), path);
      oneOf(
        item.state,
        ['ready', 'loading', 'saving', 'saved', 'error', 'locked'],
        `${path}.state`,
      );
      text(item.stateLabel, `${path}.stateLabel`);
    }
    text(item.label, `${path}.label`);
    optionalText(item.description, `${path}.description`);
    const itemBlockIds = array(item.blockIds, `${path}.blockIds`).map((id, blockIndex) => {
      const blockId = text(id, `${path}.blockIds[${blockIndex}]`);
      if (!blockIds.has(blockId))
        fail(`${path}.blockIds[${blockIndex}]`, 'must reference a declared block');
      if (owned.has(blockId))
        fail(`${path}.blockIds[${blockIndex}]`, 'must be owned by exactly one navigation item');
      owned.add(blockId);
      return blockId;
    });
    if (navigation.kind === 'linear') {
      validateStageStory(item.story, `${path}.story`, {
        blockIds: itemBlockIds,
        actions,
        descriptor,
        isActive: item.id === activeId,
      });
    }
  });
  if (navigation.kind === 'linear') {
    text(navigation.invalidationMessage, 'descriptor.navigation.invalidationMessage');
    if (currentCount !== 1)
      fail('descriptor.navigation.items', 'must contain exactly one current item');
    if (
      activeIndex < 0 ||
      object(items[activeIndex], `descriptor.navigation.items[${activeIndex}]`).status !== 'current'
    ) {
      fail('descriptor.navigation.activeId', 'must reference the current linear item');
    }
    items.slice(activeIndex + 1).forEach((item, index) => {
      if (
        object(item, `descriptor.navigation.items[${activeIndex + index + 1}]`).status ===
        'complete'
      ) {
        fail('descriptor.navigation.items', 'cannot mark a later item complete');
      }
    });
  }
  if (owned.size !== blockIds.size)
    fail('descriptor.navigation.items', 'must own every block exactly once');
}

export function assertFmFormFlowPageDescriptor(
  value: unknown,
): asserts value is FmFormFlowPageDescriptor {
  const descriptor = object(value, 'descriptor');
  exact(descriptor, ROOT_KEYS, 'descriptor');
  ['id', 'ariaLabel', 'title', 'stateLabel'].forEach((key) =>
    text(descriptor[key], `descriptor.${key}`),
  );
  optionalText(descriptor.description, 'descriptor.description');
  optionalText(descriptor.backLabel, 'descriptor.backLabel');
  oneOf(descriptor.state, ['ready', 'loading', 'empty', 'error', 'locked'], 'descriptor.state');
  if (descriptor.onEvent !== undefined && typeof descriptor.onEvent !== 'function')
    fail('descriptor.onEvent', 'must be a domain callback');
  if (descriptor.summary !== undefined) validateSummary(descriptor.summary);
  const blocks = array(descriptor.blocks, 'descriptor.blocks');
  const blockIds = uniqueIds(blocks, 'descriptor.blocks');
  blocks.forEach(validateBlock);
  const actions = array(descriptor.actions, 'descriptor.actions');
  /* Read before the navigation: a stage's state anchor names the action that
     resolves it, so the ids have to exist by then. */
  uniqueIds(actions, 'descriptor.actions');
  /* Read as RECORDS, not ids: a stage's state anchor has to apply the same
     declared-and-not-disabled rule the other state surfaces apply, and an id
     alone cannot answer "is it disabled" (review round 5). */
  const actionRecords = actions.map((item, index) => object(item, `descriptor.actions[${index}]`));
  validateNavigation(descriptor.navigation, blockIds, actionRecords, descriptor);
  actions.forEach((value, index) => {
    const path = `descriptor.actions[${index}]`;
    const action = object(value, path);
    exact(
      action,
      new Set(['id', 'label', 'variant', 'disabled', 'disabledReason', 'confirmation']),
      path,
    );
    text(action.label, `${path}.label`);
    if (action.variant !== undefined)
      oneOf(action.variant, ['default', 'primary', 'text', 'link', 'bordered'], `${path}.variant`);
    if (action.disabled !== undefined) boolean(action.disabled, `${path}.disabled`);
    optionalText(action.disabledReason, `${path}.disabledReason`);
    if (action.confirmation !== undefined) {
      validateFmActionConfirmation(action.confirmation, `${path}.confirmation`, fail);
    }
    if (action.disabled && action.disabledReason === undefined)
      fail(`${path}.disabledReason`, 'is required when disabled');
  });
  validateFormFlowStateSurface(descriptor, actionRecords);
}
